import { Logger } from 'utilities';
import { BLOCK_TAGS, LINE_BREAK_TAG, LIST_ITEM_PREFIX, LIST_ITEM_TAG } from './constants';

// Pushes a break unless one is already pending, so nested blocks and an empty
// paragraph between two others don't stack up blank lines.
const appendLineBreak = (parts: string[]) => {
  if (parts.length === 0 || parts[parts.length - 1] === '\n') {
    return;
  }

  parts.push('\n');
};

/**
 * Converts rich-text HTML to plain text, preserving the line breaks the editor
 * shows on screen: block elements and `<br>` become newlines, and list items are
 * prefixed with a dash.
 */
export const htmlToPlainText = (html: string): string => {
  if (!html) {
    return '';
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const parts: string[] = [];

    // Walk the tree instead of reading `body.textContent`: textContent joins block elements with no
    // separator at all, which would turn `<p>a</p><p>b</p>` into "ab" instead of "a\nb".
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text) {
          parts.push(text);
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const tagName = (node as HTMLElement).tagName.toLowerCase();

      if (tagName === LINE_BREAK_TAG) {
        appendLineBreak(parts);
        return;
      }

      const isListItem = tagName === LIST_ITEM_TAG;
      const isOwnLine = isListItem || BLOCK_TAGS.has(tagName);

      if (isOwnLine) {
        appendLineBreak(parts);
      }
      if (isListItem) {
        parts.push(LIST_ITEM_PREFIX);
      }

      Array.from(node.childNodes).forEach(walk);

      if (isOwnLine) {
        appendLineBreak(parts);
      }
    };

    Array.from(doc.body.childNodes).forEach(walk);

    return (
      parts
        .join('')
        // The editor stores non-breaking spaces; the PDF text layer only wants regular ones.
        .replace(/\u00A0/g, ' ')
        // Collapse each run of newlines -- plus the spaces and tabs padding it -- into a single
        // newline, matching the browser: HTML ignores that whitespace, so the PDF should too.
        .replace(/[ \t]*\n[ \t\n]*/g, '\n')
        .trim()
    );
  } catch (error) {
    Logger.warn('ExportWidgetRelay: failed to parse note HTML', error);
    return '';
  }
};
